import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import NegotiationBadge from '@/components/NegotiationBadge';
import StartNegotiationDialog from '@/components/ops/StartNegotiationDialog';
import CompleteNegotiationDialog from '@/components/ops/CompleteNegotiationDialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VendorSelectionDialog = ({ req, onClose, onChanged }) => {
  const { getAuthHeader } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');
  const [negotiationTarget, setNegotiationTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);

  const loadQuotations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotations?requisition_id=${req.id}`, { headers: getAuthHeader() });
      setQuotations(response.data);
    } catch (error) {
      toast.error('Failed to load quotations');
    }
  }, [req.id, getAuthHeader]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  const refresh = () => {
    loadQuotations();
    onChanged();
  };

  const handleApproveVendor = async () => {
    if (!selectedVendor || !selectedPrice) return;
    try {
      await axios.post(`${API}/requisitions/${req.id}/approve-vendor`, {
        vendor_name: selectedVendor,
        price: parseFloat(selectedPrice)
      }, { headers: getAuthHeader() });
      toast.success('Vendor approved and added to accumulation');
      onChanged();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve vendor');
    }
  };

  const handleDownloadNegotiatedPdf = async (quot) => {
    try {
      const response = await axios.get(`${API}/quotations/${quot.id}/negotiated-pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to download negotiated invoice');
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Vendor for {req.reagent_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-surface rounded">
              <div>
                <p className="text-sm text-text-tertiary">Catalogue Number</p>
                <p className="font-mono text-text-primary">{req.catalogue_number}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary">Project</p>
                <p className="text-text-primary">{req.project_code_name}</p>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-text-primary">Compare Quotations</h3>
              {quotations.map((quot) => (
                <div
                  key={quot.id}
                  className={`border-2 p-4 rounded cursor-pointer transition-colors ${
                    selectedVendor === quot.vendor_name
                      ? 'border-accent-brand bg-accent-brand-subtle'
                      : 'border-border-default hover:border-accent-brand'
                  }`}
                  onClick={() => {
                    setSelectedVendor(quot.vendor_name);
                    setSelectedPrice(quot.price.toString());
                  }}
                  data-testid={`quotation-option-${quot.id}`}
                >
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <NegotiationBadge quot={quot} />
                    <div className="flex gap-2 ml-auto">
                      {quot.negotiation_status !== 'negotiated' && quot.negotiation_status !== 'under_negotiation' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); setNegotiationTarget(quot); }}
                          data-testid={`start-negotiation-btn-${quot.id}`}
                        >
                          Negotiate
                        </Button>
                      )}
                      {quot.negotiation_status === 'under_negotiation' && (
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={(e) => { e.stopPropagation(); setCompleteTarget(quot); }}
                          data-testid={`complete-negotiation-btn-${quot.id}`}
                        >
                          Upload Final Quote
                        </Button>
                      )}
                      {quot.negotiation_status === 'negotiated' && quot.negotiated_pdf_file_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleDownloadNegotiatedPdf(quot); }}
                          data-testid={`view-negotiated-pdf-btn-${quot.id}`}
                        >
                          View Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-text-tertiary">Vendor</p>
                      <p className="font-medium text-text-primary">{quot.vendor_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Price</p>
                      <p className="font-semibold text-accent-brand">
                        ₹{quot.price.toFixed(2)}
                        {quot.negotiation_status === 'negotiated' && quot.original_price != null && quot.original_price !== quot.price && (
                          <span className="ml-2 text-sm text-text-tertiary line-through font-normal">₹{quot.original_price.toFixed(2)}</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Delivery</p>
                      <p className="text-text-primary">{quot.delivery_timeline}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Payment Terms</p>
                      <p className="text-text-primary">{quot.payment_terms}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button
              className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white"
              disabled={!selectedVendor}
              onClick={handleApproveVendor}
              data-testid="approve-vendor-button"
            >
              Approve {selectedVendor || 'Vendor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {negotiationTarget && (
        <StartNegotiationDialog
          target={negotiationTarget}
          onClose={() => setNegotiationTarget(null)}
          onDone={() => { setNegotiationTarget(null); refresh(); }}
        />
      )}
      {completeTarget && (
        <CompleteNegotiationDialog
          target={completeTarget}
          onClose={() => setCompleteTarget(null)}
          onDone={() => { setCompleteTarget(null); refresh(); }}
        />
      )}
    </>
  );
};

export default VendorSelectionDialog;
